import { config } from '../config';
import { isEvidenced, type Evidenced } from './evidence';
import { canPromoteAnother } from './halftime';
import { compareConfirmedFirst } from './order';
import type { RankedByStatus } from './order';

/**
 * DIST-2 … DIST-7 / §10.2 — **the distillation rules**, as pure functions (build unit U-14b).
 *
 * > *"Ranks hypotheses by `cachedCount` over the whole match, `confirmed` first. The user promotes
 * > exactly `TALKPOINT_COUNT` to `phase=fullTime` TalkPoints; the UI blocks completion with any
 * > other number … Hypotheses promoted at halftime are shown as such and carry **no privilege and
 * > no exclusion**."*
 *
 * ── What this module deliberately does NOT have, and why that is the requirement ──
 *
 * **No eligibility predicate.** HT-5 has one and this does not, and that asymmetry is stated by the
 * two requirements rather than inferred: §7.3 excludes a hypothesis the user *"has just disproved or
 * invalidated"*, and DIST-2's only sentence about exclusion is that halftime-promoted hypotheses
 * carry **none**. Adding a dismissal filter here would invent a rule the locked requirement does not
 * state, and it would move DIST-7's gate — *"fewer than `TALKPOINT_COUNT` hypotheses exist"* — onto
 * a count the requirement is not talking about, opening the source-less escape hatch on matches that
 * are not sparse.
 *
 * It is also a different *harm*. §7.3's exclusion protects a **one-tap** promotion three minutes
 * after a dismissal. Here every promotion costs DIST-3's instruction text — required, typed, with the
 * source hypothesis shown as reference — so a disproved pattern reaching the share card is a
 * sentence the user deliberately wrote about it, not a mis-tap. The row still renders its status, so
 * *"disproved"* is information the user has, not a decision this module makes for them.
 *
 * **Declared reading**, reported for a §16.4 ruling.
 *
 * ── DIST-2's "no privilege and no exclusion" is carried by a TYPE ────────────
 *
 * {@link DistillationCandidate} extends `RankedByStatus`, which has no field saying whether a
 * hypothesis was promoted at halftime — so {@link rankDistillation} **cannot** rank on it, cannot
 * float it to the top (privilege) and cannot drop it (exclusion). §17.6's clause is therefore a
 * property of the comparator's input type and not of a filter someone remembered to leave out. The
 * *"shown as such"* half is the screen's, from a separate set of ids it renders a badge from —
 * separate precisely so it never reaches this function.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The ranked list (DIST-2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What §10.2's ranking may see.
 *
 * Structurally minimal, in `order.ts`'s tradition and for its reason: the body of
 * {@link rankDistillation} can read exactly these fields, so a later edit cannot start ranking on
 * `boardPosition` (which would launder the board's composition into a read-aloud list), on
 * `statusOverride` (which would be the eligibility filter DIST-2 does not ask for), or on halftime
 * promotion (which DIST-2 forbids by name).
 */
export interface DistillationCandidate extends RankedByStatus, Evidenced {
  readonly id: string;
  readonly text: string;
}

/**
 * DIST-9 (revision 4) — **the promotion list offers only hypotheses the match produced evidence
 * for**, and DIST-7's threshold counts that same set.
 *
 * > *"A hypothesis with `cachedCount = 0` and no user-set status override is **not** offered for
 * > promotion to a `phase=fullTime` TalkPoint and is not counted toward DIST-7's threshold — a
 * > pattern nobody observed is not a finding, and promoting one as an instruction is the failure
 * > the escape hatch exists to avoid."*
 *
 * ── This is not the eligibility filter this module refused to add ──────────
 *
 * The header above records a **declared reading**: DIST-2 says halftime-promoted hypotheses carry
 * *"no privilege and no exclusion"*, and a *dismissal* filter here would invent a rule the locked
 * requirement did not state. That reading is unchanged and this does not contradict it —
 * `statusOverride` still ranks nothing and excludes nothing, and a disproved hypothesis the user
 * incremented is still offered, still showing its status, still their decision to make. What is
 * excluded is the set the user never touched **at all**, and it is excluded by a requirement that
 * now says so by name rather than by a reading of one that did not.
 *
 * ── The other half of DIST-9 is what this function does NOT do ────────────
 *
 * *"The exclusion is from one list, never from the data."* This returns a filtered **view**; every
 * excluded row is still in the match, in the archive (INV-12), in replay (INV-9), in FILE-1's team
 * view and in HT-2's sheet. *Alternative considered:* rank them last rather than exclude them —
 * **rejected**, ranking last does not restore DIST-7, and a 43-row tail of unobserved patterns
 * below six real ones is a worse instrument on the one screen where the user chooses what to tell
 * a team.
 */
export function evidencedCandidates<T extends DistillationCandidate>(
  candidates: readonly T[],
): T[] {
  return candidates.filter(isEvidenced);
}

/**
 * DIST-2 — *"ranks hypotheses by `cachedCount` over the whole match, `confirmed` first."*
 *
 * The same comparator HT-5 uses (`order.ts`), because it is the same sentence in two requirements.
 * Returns a new array; the input is never sorted in place, for the reason `discoveryOrder` gives.
 *
 * Unlike `rankAdjustments` there is **no filter** — see this module's header for why that is DIST-2
 * being implemented rather than a filter being forgotten.
 */
export function rankDistillation<T extends DistillationCandidate>(candidates: readonly T[]): T[] {
  return [...candidates].sort(compareConfirmedFirst);
}

// ─────────────────────────────────────────────────────────────────────────────
// INV-6's two bounds, as one answer the surface can render (DIST-2)
// ─────────────────────────────────────────────────────────────────────────────

/** What the distillation surface needs to know about its own selection. */
export interface SelectionState {
  /** INV-6's upper bound — `false` at `TALKPOINT_COUNT`, so a fourth promote is refused before it is attempted. */
  readonly canPromote: boolean;
  /** DIST-2's *exactly* — the only number that may complete. */
  readonly canComplete: boolean;
  /** How many more are needed, for the control's own explanation of why it is unavailable. */
  readonly remaining: number;
}

/**
 * DIST-2 — *"the UI blocks completion with any other number (INV-6)."*
 *
 * ── Two bounds, and only one of them is new ─────────────────────────────────
 *
 * The **upper** bound is INV-6's and is phase-agnostic: `canPromoteAnother` already states it, at
 * HT-5, its first consumer. Imported rather than restated — two expressions of one bound is how the
 * halftime surface and this one come to disagree about what *full* means, and `writeSelection` would
 * then refuse a promotion the screen had already ticked.
 *
 * The **exactly** is genuinely new: INV-6 is *"unconstrained from 0 to `TALKPOINT_COUNT`"* at
 * halftime (§17.6) and *exactly* `TALKPOINT_COUNT` at full time. So halftime has no `canComplete`
 * and there is nothing here to reuse.
 *
 * **This is the second layer, never the only one.** §10.2 is explicit: *"the UI blocks completion
 * with any other number, **and** `CompleteDistillation` carries an in-transaction postcondition
 * asserting the same (INV-6 is not a UI rule)."* Both exist — `machine.ts`'s `ERR_TALKPOINT_SET`
 * guard produces the renderable refusal, `talkPoints.assertInv6` aborts the transaction — and this
 * is what the user reads before either can fire.
 */
export function selectionState(selectedCount: number): SelectionState {
  return {
    canPromote: canPromoteAnother(selectedCount),
    canComplete: selectedCount === config.TALKPOINT_COUNT,
    remaining: Math.max(0, config.TALKPOINT_COUNT - selectedCount),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DIST-7 — the escape hatch
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DIST-7 — *"When fewer than `TALKPOINT_COUNT` hypotheses exist for a match, the user may author a
 * TalkPoint with no source hypothesis, so a sparse match still reaches `archived`. This is an escape
 * hatch, not a primary flow, and MUST NOT be surfaced as an equal-weight option when 3 or more
 * hypotheses exist."*
 *
 * ── Two clauses, and the reading that satisfies both ────────────────────────
 *
 * The requirement grants a **permission** (*"when fewer than three … may author"*) and states a
 * **prominence** MUST-NOT (*"not an equal-weight option when 3 or more exist"*). §10.2 adds that it
 * is *"a de-emphasized escape hatch reached from an overflow"* — unconditionally, not only when the
 * match is rich.
 *
 * So: the affordance lives behind an overflow in **every** case, which discharges the prominence
 * clause on its own; and it is **enabled only below the threshold**, which is the permission clause
 * read literally. That is strictly stronger than the MUST-NOT and exactly the permission.
 *
 * ── Re-based by DIST-9 (revision 4), and this is the change ────────────────
 *
 * The gate was on the number of hypotheses **on the match** — DIST-7's own words at the time. PRE-9
 * pre-selects 49 of them, so *"fewer than three exist"* became **false of every match ever
 * created** and this function returned `false` unconditionally: the escape hatch was unreachable,
 * permanently, with nothing anywhere reporting it. The match is not deadlocked, which is exactly
 * why it is dangerous rather than merely broken — DIST-2 would still let the user promote any three
 * rows, so the visible path out of a sparse match becomes *"promote three patterns nobody
 * observed"*, dressed as findings, onto the share card that is the product's entire output (P-3).
 *
 * DIST-7's prohibition clause is struck and **DIST-9** appended: the predicate now counts the
 * **evidenced** set — `cachedCount > 0` or a user-set override — which is the same set
 * {@link evidencedCandidates} offers. The escape-hatch character is unchanged; only the predicate
 * that reveals it moves. The parameter is renamed so a caller cannot pass the old number by
 * accident: `hypothesisCount` and `evidencedCount` are different quantities and now read as such.
 *
 * ── Why this is enforced in the handler too, and not only here ──────────────
 *
 * Design §4 / §19 F-30: *"an invariant enforced only by a UI is not an invariant."* A source-less
 * full-time talk point on a match that recorded eight patterns is a share card that discards the
 * whole match's evidence, and it severs DIST-6's `sourceHypothesisId` link that FILE-1 reads. The
 * *prominence* half cannot be enforced anywhere but a surface; the *permission* half can, so
 * `commands/distill.ts` refuses it as well.
 *
 * **Declared reading**, reported for a §16.4 ruling.
 */
export function canAuthorSourceless(evidencedCount: number): boolean {
  return evidencedCount < config.TALKPOINT_COUNT;
}
